'use client'

import {
  createContext,
  startTransition,
  useContext,
  useEffect,
  useOptimistic,
  useRef,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { useRouter } from 'next/navigation'

import {
  searchParamsFromUrl,
  toCatalogSearchParams,
  toQueryString,
  type CatalogSearchParams,
} from '../lib/search-params'
import { useCatalogQuery } from './catalog-query'

type Change = (current: CatalogSearchParams) => Partial<CatalogSearchParams>

type CatalogFiltersContextValue = {
  /** Отбор для показа отметок: меняется сразу по клику, не дожидаясь ответа сервера. */
  current: CatalogSearchParams
  /**
   * Меняет отбор и уводит на новый адрес. `replace` заменяет запись в истории браузера вместо
   * добавления новой — так делает поиск города, иначе кнопка «назад» отматывала бы по букве.
   */
  navigate: (change: Change, options?: { replace?: boolean }) => void
  /** Готовые свойства ссылки, меняющей отбор: настоящий адрес плюс перехват обычного клика. */
  linkProps: (change: Change) => { href: string; onClick: (event: MouseEvent<HTMLAnchorElement>) => void }
}

const CatalogFiltersContext = createContext<CatalogFiltersContextValue | null>(null)

/**
 * Обычный клик по ссылке перехватываем, чтобы посчитать отбор от свежего снимка. Клик с Ctrl, Cmd,
 * Shift, Alt и средней кнопкой отдаём браузеру — иначе сломается «открыть в новой вкладке».
 */
export const isPlainClick = (event: MouseEvent<HTMLAnchorElement>) =>
  !event.defaultPrevented && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey

/**
 * Единственный источник отбора для всего каталога: и отметки фильтров, и город, и порядок списка,
 * и поиск считают следующий адрес отсюда.
 *
 * Зачем это нужно. Отбор, пришедший с сервера, отстаёт: он обновляется только после ответа и новой
 * отрисовки. Пока ответ едет, человек успевает щёлкнуть ещё раз, и если считать от серверного
 * значения, второй клик посчитает изменение без первого — а поскольку повторная отметка снимает
 * значение, выбор молча пропадёт.
 */
export const CatalogFiltersProvider = ({
  serverParams,
  children,
}: {
  serverParams: CatalogSearchParams
  children: ReactNode
}) => {
  const router = useRouter()
  const { query } = useCatalogQuery()
  // Снимок для вычислений. Именно ссылка, а не состояние: между двумя быстрыми кликами перерисовки
  // может не случиться, и обработчик прочитал бы старое значение — ровно та потеря, которую чиним.
  const draftRef = useRef(serverParams)
  /** Адрес, отправленный последним. Пусто — своих переходов в пути нет. */
  const sentQueryRef = useRef('')
  // Показ отметок: React сам вернёт значение к серверному, когда переход завершится.
  const [current, showChosen] = useOptimistic(serverParams)

  useEffect(() => {
    const serverQuery = toQueryString(serverParams, {})
    // Серверный отбор принимаем, только когда наш переход уже доехал. Иначе он старее снимка:
    // это ответ на предыдущий клик, а человек успел щёлкнуть ещё раз.
    if (sentQueryRef.current === '' || sentQueryRef.current === serverQuery) {
      draftRef.current = serverParams
      sentQueryRef.current = ''
    }
  }, [serverParams])

  useEffect(() => {
    // Кнопки «назад» и «вперёд»: страница пришла не от нас, поэтому снимок берём прямо из адреса.
    const acceptHistoryMove = () => {
      draftRef.current = toCatalogSearchParams(searchParamsFromUrl(window.location.search))
      sentQueryRef.current = ''
    }

    window.addEventListener('popstate', acceptHistoryMove)
    return () => window.removeEventListener('popstate', acceptHistoryMove)
  }, [])

  /** Набранный в строке поиска текст переносим в адрес вместе с фильтром, иначе он потеряется. */
  const urlFor = (params: CatalogSearchParams) =>
    toQueryString({ ...params, query: query.trim() || undefined }, { page: undefined })

  const navigate = (change: Change, options?: { replace?: boolean }) => {
    const next = { ...draftRef.current, ...change(draftRef.current) }
    const url = urlFor(next)
    draftRef.current = next
    sentQueryRef.current = url

    startTransition(() => {
      showChosen(next)
      if (options?.replace) {
        router.replace(url, { scroll: false })
      } else {
        router.push(url, { scroll: false })
      }
    })
  }

  const value: CatalogFiltersContextValue = {
    current,
    navigate,
    linkProps: (change) => ({
      // Адрес строится от показанного отбора, а не от снимка: во время отрисовки ссылку читают,
      // а не кликают, и обращаться к ссылочному значению здесь нельзя. Настоящий клик всё равно
      // перехватывается и считает от снимка.
      href: urlFor({ ...current, ...change(current) }),
      onClick: (event) => {
        if (!isPlainClick(event)) {
          return
        }

        event.preventDefault()
        navigate(change)
      },
    }),
  }

  return <CatalogFiltersContext value={value}>{children}</CatalogFiltersContext>
}

export const useCatalogFilters = () => {
  const context = useContext(CatalogFiltersContext)
  if (!context) {
    throw new Error('useCatalogFilters используется вне CatalogFiltersProvider')
  }

  return context
}
